import { Injectable } from '@angular/core';
import { Urls } from '@core/config/urls';
import { LegacyBaseApiService } from '@smartera/core';
import { isObjectNotNull } from '@core/utils';
import { Observable } from 'rxjs';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import { map } from 'rxjs/operators';
import { ElasticSearchQuery, Key } from '../models';
import { LabelMetaData } from '../models/label-meta-data';
import {
	ElasticSearchAggregationValue,
	ElasticSearchData,
	ElasticSearchDataBucket,
	ElasticSearchResponse,
} from '../pivot/models';
import { ReportUtils } from '../utils';

@Injectable({
	providedIn: 'root',
})
export class ElasticSearchService extends LegacyBaseApiService<any> {
	totalRecords = 0;

	public searchByQuery(id: string, query: ElasticSearchQuery): Observable<any> {
		return this._restClientService.post(`${Urls.BASE_URL.toString()}/es-proxy/${id}/_search`, JSON.stringify(query));
	}

	public getColumnMetadata(id: string): Observable<any> {
		return this._restClientService.get(`${Urls.BASE_URL.toString()}/es-proxy/${id}/_mapping`).pipe(
			map((result) => {
				const alias = Object.keys(result);
				const [prop] = Object.keys(result[alias[0]].mappings);
				return result[alias[0]].mappings.properties;
			})
		);
	}

	public createVirtualColumn(indexId: string, virtualColumn: VirtualColumn): Observable<any> {
		return forkJoin(
			this._restClientService.post(
				`${Urls.BASE_URL.toString()}/es-proxy/${indexId}/_mapping`,
				JSON.stringify({
					properties: {
						[virtualColumn.key]: {
							type: 'double',
						},
					},
				})
			),
			this._restClientService.post(
				`${Urls.BASE_URL.toString()}/es-proxy/${indexId}/_update_by_query`,
				JSON.stringify({
					script: {
						source: this.prepareExpression(virtualColumn),
						lang: 'painless',
					},
				})
			)
		);
	}

	public updateVirtualColumn(indexId: string, virtualColumn: VirtualColumn): Observable<any> {
		return this._restClientService.post(
			`${Urls.BASE_URL.toString()}/es-proxy/${indexId}/_update_by_query`,
			JSON.stringify({
				script: {
					source: this.prepareExpression(virtualColumn),
					lang: 'painless',
				},
			})
		);
	}

	private prepareExpression(virtualColumn: VirtualColumn): string {
		const condition = virtualColumn.expression
			.filter((item: string) => {
				return item.startsWith('ctx._source.');
			})
			.map((item: string) => item + ' != null ')
			.join(' && ');
		return `if (${condition}) {ctx._source.${virtualColumn.key}=${virtualColumn.expression.join('')};}`;
	}

	/*
		DEPRECATED!!
		TODO: Kaldırılacak!
 */
	public getAggsData(id: string, filter: any, aggs: any, anysData?, lbm?: LabelMetaData): Observable<any> {
		const must_not = [];
		if (anysData) {
			const fields = [...anysData.columns, ...anysData.rows];
			fields.forEach((field) => {
				if (field.value === lbm[field.key].nullValue) {
					must_not.push({ exists: { field: field.key } });
				}
			});
		}
		let filterData = filter.query;
		if (filterData) {
			filterData = {
				must: [...filterData],
				must_not,
			};
		}
		const query = {
			query: {
				bool: {
					...filterData,
				},
			},
			size: 0,
			aggs,
		};
		return this._restClientService
			.post(`${Urls.BASE_URL.toString()}/es-proxy/${id}/_search`, JSON.stringify(query))
			.pipe(
				map((result) => {
					//TODO: Yeni component tamamlandığında response data olarak dönmeli!
					const data = this.transformPivotData(result.aggregations);
					return result.aggregations;
				})
			);
	}

	public getAggregationData(id: string, filter: any, aggs: any, anysData?, lbm?: LabelMetaData): Observable<any> {
		const body: ElasticSearchQuery = {
			query: ReportUtils.generateElasticSearchFilterQuery(filter, anysData, lbm),
			size: 0,
			aggs,
		};
		return this.searchByQuery(id, body).pipe(map((result) => this.transformPivotData(result.aggregations)));
	}

	public getOtherAggregationData(
		id: string,
		fields: Key[],
		filter: any,
		aggs: any,
		anysData?,
		lbm?: LabelMetaData
	): Observable<any> {
		const body = {
			query: ReportUtils.generateElasticSearchFilterQuery(filter, anysData, lbm),
			size: 0,
			aggs,
		};
		return this.searchByQuery(id, body).pipe(
			map((result) => {
				const otherFieldIndex = fields.findIndex((field: Key) => field?.showOtherValues);
				const otherGroupFields = fields.slice(0, otherFieldIndex);
				const otherField = fields[otherFieldIndex];

				const data = [];
				const buckets = result.aggregations.other_filter.buckets;
				Object.keys(buckets).forEach((key: string) => {
					const otherFieldValues = key.split(ReportUtils.OTHER_FIELD_SEPARATOR).splice(1);
					const item = {};
					otherGroupFields.forEach((field: Key, index) => {
						item[field.name] = otherFieldValues[index];
					});
					item[otherField.name] = otherField.otherValuesTitle;

					const transformData = this.transformPivotData(buckets[key]);
					transformData.forEach((transformItem) => {
						data.push({
							...item,
							...transformItem,
						});
					});
				});
				return data;
			})
		);
	}

	private transformPivotData(aggregations: ElasticSearchResponse): object[] {
		const transformedData: object[] = [];
		let transformedItem: object;
		const topLevelAggregationFieldNames: string[] = Object.keys(aggregations);
		// debugger;
		topLevelAggregationFieldNames.forEach((fieldName: string, index: number) => {
			const aggregation: ElasticSearchData = aggregations[fieldName];
			const buckets = aggregation.buckets;

			if (buckets) {
				/* Satır veya Sütun alanı içeren raporlar */
				buckets.forEach((bucket) => {
					transformedItem = { [fieldName]: bucket.key };
					const subFieldNames = this.filterSubFieldNames(bucket);

					// Alt bucket var mı kontrol et!
					let subBuckets = null;
					let subFieldName = null;
					subFieldNames.forEach((fieldName: string) => {
						if ((<ElasticSearchData>bucket[fieldName]).buckets) {
							subBuckets = (<ElasticSearchData>bucket[fieldName]).buckets;
							subFieldName = fieldName;
						}
					});

					if (subBuckets) {
						/* Birden fazla satır veya sütundan oluşan bir raporlar */
						this.transformNestedData(transformedData, transformedItem, subBuckets, subFieldName);
					} else {
						subFieldNames.forEach((subFieldName: string, subFieldIndex: number) => {
							/* Tek bir satır veya sütundan oluşan bir raporlar */

							//NOTE: bucket olmayıp bir alt node'unda key ve value içeren object olabilir. Bunlar için bir alt node'a inip value'yu almak gerekiyor.
							const subFieldData = <ElasticSearchAggregationValue>bucket[subFieldName];
							let subFieldValue: string | number;
							if (isObjectNotNull(subFieldData.value)) {
								subFieldValue = subFieldData.value;
							} else {
								const nestedSubFieldKeys = this.filterSubFieldNames(subFieldData);
								if (nestedSubFieldKeys[0]) {
									subFieldValue = subFieldData[nestedSubFieldKeys[0]].value;
								}
							}

							transformedItem = {
								...transformedItem,
								[subFieldName]: subFieldValue,
							};
							if (subFieldIndex === subFieldNames.length - 1) {
								transformedData.push(transformedItem);
							}
						});
					}
				});
			} else {
				/* Satır ve Sütun alanı içermeyen raporlar */
				transformedItem = {
					...transformedItem,
					[fieldName]: aggregation.value,
				};
				if (index === topLevelAggregationFieldNames.length - 1) {
					transformedData.push(transformedItem);
				}
			}
		});
		return transformedData;
	}

	private transformNestedData(
		transformedData: object[],
		parentItem: object,
		buckets: ElasticSearchDataBucket[],
		fieldName: string
	): void {
		buckets.forEach((bucket) => {
			const transformedItem: object = { ...parentItem };
			transformedItem[fieldName] = bucket.key;
			const subFieldNames = this.filterSubFieldNames(bucket);
			for (const subFieldName of subFieldNames) {
				if ((<ElasticSearchData>bucket[subFieldName]).buckets) {
					const subBuckets = (<ElasticSearchData>bucket[subFieldName]).buckets;
					this.transformNestedData(transformedData, transformedItem, subBuckets, subFieldName);
					return;
				} else {
					transformedItem[subFieldName] = (<ElasticSearchAggregationValue>bucket[subFieldName]).value;
				}
			}
			transformedData.push(transformedItem);
		});
	}

	private filterSubFieldNames(bucket: ElasticSearchDataBucket | ElasticSearchAggregationValue): string[] {
		return Object.keys(bucket).filter(
			(key) => key !== 'key' && key !== 'doc_count' && key !== 'key_as_string' && key !== 'to' && key !== 'from'
		);
	}

	/*
		REFACTOR ?: 
		- Düz liste oluşturmak için es-proxy service'in _search API'ına istek atan method.
		- Gelen parametreler kontrol edilecek, parametre değelerine uygun type'lar verilecek!
		- Fonksiyon yeniden düzenlenecek.
 */
	public getData(
		id: string,
		filter: any,
		anysData?,
		labelMetaData?: LabelMetaData,
		sortField?: string,
		sortOrder?: 'DESC' | 'ASC'
	): Observable<any> {
		const body: ElasticSearchQuery = {
			query: ReportUtils.generateElasticSearchFilterQuery(filter, anysData, labelMetaData),
			_source: filter._source,
			from: filter.from,
			size: filter.size,
			sort: this.prepareSort(sortField, sortOrder, labelMetaData),
		};
		return this.searchByQuery(id, body).pipe(
			map((result) => {
				const data = this.transformFlatListData(result);
				return this.prepareFlatListData(data, labelMetaData);
			})
		);
	}

	private transformFlatListData(data): object[] {
		if (data.hits) {
			this.totalRecords = data.hits.total.value;
			if (data.hits.hits && data.hits.hits instanceof Array) {
				return (data.hits.hits as Array<any>).map((item) => item._source);
			} else {
				return [];
			}
		} else {
			return [];
		}
	}

	private prepareFlatListData(data: unknown[], labelMetaData: LabelMetaData): any[] {
		const metaDataKeys = Object.keys(labelMetaData);
		return data.map((item) => {
			metaDataKeys.forEach((key: string) => {
				if (labelMetaData[key].type === 'date') {
					item[key] = new Date(item[key]);
				}
			});
			return item;
		});
	}

	private prepareSort(sortField: string, sortOrder: 'DESC' | 'ASC' = 'DESC', labelMetaData: LabelMetaData): object[] {
		if (sortField) {
			const value = {};
			if (labelMetaData[sortField].type === 'text') {
				value[sortField + '.keyword'] = {
					order: sortOrder,
				};
			} else {
				value[sortField] = {
					order: sortOrder,
				};
			}
			return [value];
		} else {
			[];
		}
	}

	getSmartyGptData(id: string, query: ElasticSearchQuery): Observable<object[]> {
		const data = this.searchByQuery(id, query);
		if (query.aggs) {
			return data.pipe(map((result) => this.transformPivotData(result.aggregations)));
		} else {
			return data.pipe(map((result) => this.transformFlatListData(result)));
		}
	}

	public getFieldsInfo(id: string, fields: string[], filterData?: any): Observable<object[]> {
		// 👇 generate the aggs query base on fields keys
		const aggsFields = fields
			.map((key) => {
				return {
					[`${key}_count`]: {
						cardinality: {
							field: `${key}.keyword`,
						},
					},
					[`${key}_data`]: {
						terms: {
							field: `${key}.keyword`,
							size: 101,
						},
					},
				};
			})
			.reduce((preValue, nextValue) => {
				return {
					...preValue,
					...nextValue,
				};
			}, {});

		let query = {};

		if (filterData) {
			query = {
				query: {
					bool: {
						must: [...filterData],
					},
				},
				size: 0,

				// 🤛 ⚡
				aggs: {
					...aggsFields,
				},
			};
		} else {
			query = {
				// 👈 👀 req for the api
				query: {
					bool: {},
				},
				size: 0,

				// 🤛 ⚡
				aggs: {
					...aggsFields,
				},
			};
		}

		return this._restClientService
			.post(`${Urls.BASE_URL.toString()}/es-proxy/${id}/_search`, JSON.stringify(query))
			.pipe(map((respond) => respond.aggregations));
	}
}

export interface VirtualColumn {
	key: string;
	expression: string[];
}
